import { Alert, message, Modal, Row } from 'antd';
import { UserAuthenticationContext } from 'components/context/UserAuthenticationProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Hike } from 'services/hitchit/types/hike/Hike';
import { savePreferences } from 'services/hitchit';
import deepEqual from 'components/utils/deepEqual';
import * as Sentry from '@sentry/node';
import { useIntl } from 'react-intl';
import { useQuery, useQueryClient } from 'react-query';
import { getPreferenceChoices } from 'services/fetcher/hikeCalls';
import { isDummyPreview, PreviewOrderResponse } from 'services/hitchit/types/usecases/Preference';
import { useQueryResult } from 'components/utils/handleQueryResult';
import {
  ContactFromSale,
  DealFromSale,
  InvoiceFromSale,
  ProductFromSale,
} from 'services/hitchit/types/usecases/OrderToDeal';
import TriggerOnlineSalesCreateInvoicePreferences from './TriggerOnlineSalesCreateInvoicePreferences';
import { Choice, ChoiceItem, Choices, Preferences } from 'services/hitchit/types/Choices';

const SAVE_CHANGES_KEY = 'SAVE_CHANGES_KEY';
const PREFERENCE_CHOICES_QUERY = `preferenceChoices`;

const PreferencesComponent = ({
  hike,
  onHasPendingChanges,
}: {
  hike: Hike;
  onHasPendingChanges?: (hasPendingChanges?: Preferences) => void;
}): JSX.Element => {
  const { formatMessage } = useIntl();

  const { user } = useContext(UserAuthenticationContext);
  const [showModal, setShowModal] = useState(false);

  const queryClient = useQueryClient();
  const query = useQuery([PREFERENCE_CHOICES_QUERY, { id: hike.id, user }], () =>
    getPreferenceChoices({ id: hike.id, user })
  );

  const { componentToRender } = useQueryResult({ data: query.data, error: query.error });

  const [selectedOrderPreview, setSelectedOrderPreview] = useState<
    DealFromSale | ContactFromSale | InvoiceFromSale | undefined
  >();

  const [selectedProductPreview, setSelectedProductPreview] = useState<
    ProductFromSale | undefined
  >();
  useEffect(() => {
    if (
      query.data?.previewResponseWrapper &&
      query.data.previewResponseWrapper.previews.find(isDummyPreview) &&
      !showModal
    ) {
      // only show this modal once
      setShowModal(true);
      Modal.info({
        content: (
          <div>
            <h3>
              {formatMessage({
                defaultMessage: 'No existing orders in your store yet.',
              })}
            </h3>
            <p>
              {formatMessage({
                defaultMessage: 'Therefore, not all functionality will be optimal.',
              })}
            </p>
            <p>
              {formatMessage({
                defaultMessage:
                  'But to help you, we use a fake order and/or product to construct your preferences.',
              })}
            </p>
            <p>
              {formatMessage({
                defaultMessage:
                  'Once you have your first order and product in your shop, you will be able to use this integration to the fullest.',
              })}
            </p>
          </div>
        ),
      });
    }
    if (!selectedOrderPreview && query?.data?.previewResponseWrapper?.previews?.length) {
      setSelectedOrderPreview(
        query.data.previewResponseWrapper.previews.find(
          (preview) => preview.type !== 'productFromSale'
        ) as DealFromSale | ContactFromSale | InvoiceFromSale
      );

      setSelectedProductPreview(
        query.data.previewResponseWrapper.previews.find(
          (preview) => preview.type === 'productFromSale'
        ) as ProductFromSale
      );
    }
  }, [query.data, selectedOrderPreview, formatMessage, showModal]);

  const [preferences, setPreferences] = useState<Preferences>();
  const initialPreferences = useRef<Preferences>();
  const preferencesVersion = useRef<number>();
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  useEffect(() => {
    if (onHasPendingChanges) {
      onHasPendingChanges(isUpdated ? preferences : undefined);
    }
  }, [onHasPendingChanges, isUpdated, preferences]);

  useEffect(() => {
    if (!selectedOrderPreview?.crm.contact) {
      Sentry.captureMessage('User has an order without customer, foresee this case!', {
        user: {
          hikeId: hike.id,
          firebaseUser: user?.uid,
          email: user?.email as string,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderPreview]);

  const saveChanges = async () => {
    if (hike && preferences && user) {
      setSavingChanges(true);
      message.loading({
        content: formatMessage({
          defaultMessage: 'Saving preferences',
          description: '/onboarding/preferences/save/message',
        }),
        key: SAVE_CHANGES_KEY,
      });
      await savePreferences(hike.id, preferences, user);
      queryClient.invalidateQueries();
    }
  };

  useEffect(() => {
    const updatePreferences = () => {
      initialPreferences.current = hike.preferences;
      preferencesVersion.current = hike.preferencesVersion;
      setPreferences(hike.preferences);
    };

    if (!query.data || !hike) {
      return;
    }

    if (!preferences) {
      updatePreferences();
      return;
    }
    if (preferencesVersion.current !== hike.preferencesVersion) {
      updatePreferences();
      setSavingChanges(false);
      message.success({ content: 'Preferences updated', key: SAVE_CHANGES_KEY });
    }
  }, [preferences, hike, query.data, setPreferences, savingChanges]);

  useEffect(() => {
    setIsUpdated(!deepEqual(initialPreferences.current, preferences));
  }, [hike.preferences, preferences]);

  useEffect(() => {
    if (!query.data?.choices) {
      return;
    }

    const hasRequiredChoicesNotFilled =
      Object.entries(query.data.choices).findIndex(([choiceKey, choice]) => {
        if (!choice.required) {
          return false;
        }
        if (!preferences || typeof preferences[choiceKey] === 'undefined') {
          // check on preference===undefined, because preference can be boolean as well
          return true;
        }

        const currentPreference = preferences[choiceKey];

        if (Array.isArray(currentPreference)) {
          return currentPreference.length === 0;
        }
      }) === -1;

    setIsFulfilled(hasRequiredChoicesNotFilled);
  }, [preferences, query.data?.choices]);

  const [isFulfilled, setIsFulfilled] = useState<boolean>(false);

  function onResetPreferences(key: string) {
    const digChoice = (): Choice<ChoiceItem> | undefined => {
      if (!preferences || !query.data?.choices) {
        return;
      }
      const keys = key.split('.');
      if (!keys || keys.length === 0) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return keys.reduce(
        (prevChoice: Choices | Choice<ChoiceItem> | undefined, currKey: string, index: number) => {
          if (!prevChoice) {
            return;
          }

          if (index + 1 === keys.length) {
            return (prevChoice as unknown as Choices)[currKey];
          }

          if ((prevChoice as unknown as Choice<ChoiceItem>).type) {
            const choice = prevChoice as unknown as Choice<ChoiceItem>;
            return choice.dependencies ? choice.dependencies[currKey] : undefined;
          }

          return (prevChoice as unknown as Choices)[currKey];
        },
        query.data.choices as Choices
      );
    };

    const currentChoice = digChoice();

    if (!currentChoice) {
      return;
    }
    const currentKeyPreference = currentChoice.default;

    const resettedPreferences = {
      ...preferences,
      [key]: currentKeyPreference,
    };
    setPreferences(resettedPreferences as Preferences);
  }

  function onCancelChange(key?: string) {
    if (!key) {
      // cancelAllChanges
      setPreferences(initialPreferences.current);
      return;
    }
    if (preferences && hike?.preferences && typeof hike.preferences[key] !== 'undefined') {
      const resettedPreferences = {
        ...preferences,
        [key]: hike.preferences[key],
      };
      setPreferences(resettedPreferences as Preferences);
    }
  }

  if (componentToRender) {
    return componentToRender;
  }

  const getPreferences = () => {
    return (
      <TriggerOnlineSalesCreateInvoicePreferences
        isUpdated={isUpdated}
        savingChanges={savingChanges}
        isFulfilled={isFulfilled}
        onCancelChange={onCancelChange}
        preferences={preferences}
        selectedPreview={selectedOrderPreview as InvoiceFromSale}
        onSetSelectedPreview={setSelectedOrderPreview}
        selectedProductPreview={selectedProductPreview}
        onSetSelectedProductPreview={setSelectedProductPreview}
        onClearPreferences={(key: string) => {
          const clearPreferences = { ...preferences };
          delete clearPreferences[key];
          setPreferences(clearPreferences);
        }}
        onResetPreferences={onResetPreferences}
        onSaveChanges={saveChanges}
        choices={query.data?.choices}
        previewOrders={
          query.data?.previewResponseWrapper?.previews.filter(
            (p) => p.type !== 'productFromSale'
          ) as PreviewOrderResponse[]
        }
        previewProducts={
          query.data?.previewResponseWrapper?.previews.filter(
            (p) => p.type === 'productFromSale'
          ) as ProductFromSale[]
        }
        savedPreferences={initialPreferences.current || {}}
        onSetPreferences={(updatedPreferences: Preferences) =>
          setPreferences({
            ...preferences,
            ...updatedPreferences,
          })
        }
      />
    );
  };

  return (
    <Row gutter={[32, 32]} justify="space-between">
      <Alert
        style={{ padding: 8, margin: 8 }}
        message={'Open the preference you want to change by clicking on it.'}
        type="info"
        showIcon
      />
      {getPreferences()}
    </Row>
  );
};

export default PreferencesComponent;
